import { ColorLabelEnum, hexColor } from '@utils/map/map.constants';
import { CategoryEnum } from '@utils/sensors/configuration.constants';
import { hasSpecificState } from '@utils/sensors/sensors.utils';
import { unitLabel, Units } from '@utils/unit.utils';

import { SensorOrSubSensorConfiguration } from '@/types/sensor/configuration.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';
import {
  ConnectorStatusEnum,
  DataAvailabilityEnum,
  HealthStatusEnum,
  MetricStatusValue,
  SensorTypeEnum,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';

import { MetricValue } from './detail/global/section/MetricsModule';
import {
  categoryFromSensorFamily,
  CriticalityCategoryEnum,
  criticalityCategoryFromSensorStatus,
  MonitoringLadTypeEnum,
  MonitoringPerimTypeEnum,
  MonitoringSensorType,
  sensorFamilyFromMonitoringSensorType,
  SensorOrSubSensorInfo,
  SensorOrSubSensorInfoByType,
  SensorOrSubSensorInfoWithoutSection,
} from './monitoring.types';

export function isLadType(type: MonitoringSensorType): type is MonitoringLadTypeEnum {
  return Object.values(MonitoringLadTypeEnum).includes(type as MonitoringLadTypeEnum);
}

export function isPerimType(type: MonitoringSensorType): type is MonitoringPerimTypeEnum {
  return Object.values(MonitoringPerimTypeEnum).includes(type as MonitoringPerimTypeEnum);
}

export function isSensorOrSubSensorInfoOfType<T extends SensorTypeEnum | SubSensorTypeEnum>(
  sensorOrSubSensorInfo: SensorOrSubSensorInfoByType<SensorTypeEnum | SubSensorTypeEnum>,
  type: T,
): sensorOrSubSensorInfo is SensorOrSubSensorInfoByType<T> {
  return sensorOrSubSensorInfo.type === type;
}

export function isNotSectionTypeSensorOrSubInfo(
  sensorOrSubSensorInfo: SensorOrSubSensorInfo,
): sensorOrSubSensorInfo is SensorOrSubSensorInfoWithoutSection {
  return sensorOrSubSensorInfo.configuration.type !== SubSensorTypeEnum.AUTOMATON_SEGMENT;
}

export function getSensorCriticality(sensorOrSubSensorInfo: SensorOrSubSensorInfo) {
  if (!sensorOrSubSensorInfo.active) {
    return CriticalityCategoryEnum.INACTIVE;
  } else if (sensorOrSubSensorInfo.maintenance) {
    return CriticalityCategoryEnum.MAINTENANCE;
  } else {
    if (!sensorOrSubSensorInfo.status) {
      return CriticalityCategoryEnum.UNREACHABLE;
    }
    if (
      hasSpecificState(sensorOrSubSensorInfo.status) &&
      sensorOrSubSensorInfo.status.specificState === SpecificStateEnum.SLEEP
    ) {
      return CriticalityCategoryEnum.SLEEP;
    }
    return criticalityCategoryFromSensorStatus[sensorOrSubSensorInfo.status.effectiveStatus];
  }
}

export function sortSensorByType(
  sensorConfiguration1: SensorOrSubSensorConfiguration,
  sensorConfiguration2: SensorOrSubSensorConfiguration,
) {
  return (
    sensorConfiguration1.type.localeCompare(sensorConfiguration2.type) ||
    sensorConfiguration1.name.localeCompare(sensorConfiguration2.name)
  );
}

export function isHalfActiveFamily(
  family: SensorFamilyEnum,
  ladCategories: Map<MonitoringLadTypeEnum, boolean>,
  perimCategories: Map<MonitoringPerimTypeEnum, boolean>,
) {
  return Object.entries(sensorFamilyFromMonitoringSensorType)
    .filter(([, value]) => value === family)
    .some(([key]) =>
      isLadType(key as MonitoringSensorType)
        ? ladCategories.get(key as MonitoringLadTypeEnum)
        : perimCategories.get(key as MonitoringPerimTypeEnum),
    );
}

export function isHalfActiveCategory(
  category: CategoryEnum,
  ladTypes: Map<MonitoringLadTypeEnum, boolean>,
  perimTypes: Map<MonitoringPerimTypeEnum, boolean>,
) {
  return Object.entries(categoryFromSensorFamily)
    .filter(([, value]) => value === category)
    .some(([key]) => isHalfActiveFamily(key as SensorFamilyEnum, ladTypes, perimTypes));
}

export function updateTypeMap<K extends MonitoringLadTypeEnum | MonitoringPerimTypeEnum>(
  map: Map<K, boolean>,
  isCategory: boolean,
  isActive: boolean,
): Map<K, boolean> {
  return new Map([...map].map(([type, value]) => [type, isCategory ? isActive : value]));
}

export function updateTypes<K extends MonitoringSensorType>(
  map: Map<K, boolean>,
  family: SensorFamilyEnum,
  isActive: boolean,
): Map<K, boolean> {
  return new Map(
    [...map].map(([type, value]) => [type, sensorFamilyFromMonitoringSensorType[type] === family ? isActive : value]),
  );
}

export function toggleType<K extends MonitoringLadTypeEnum | MonitoringPerimTypeEnum>(
  map: Map<K, boolean>,
  type: K,
): Map<K, boolean> {
  const newMap = new Map(map);
  newMap.set(type, !newMap.get(type));
  return newMap;
}

export function computeFamilyActive(
  ladTypes: Map<MonitoringLadTypeEnum, boolean>,
  perimTypes: Map<MonitoringPerimTypeEnum, boolean>,
  family: SensorFamilyEnum,
): boolean {
  return [...ladTypes, ...perimTypes]
    .filter(([type]) => sensorFamilyFromMonitoringSensorType[type] === family)
    .every(([, active]) => active);
}

export function computeCategoryActive(families: Map<SensorFamilyEnum, boolean>, category: CategoryEnum): boolean {
  return [...families].filter(([fam]) => categoryFromSensorFamily[fam] === category).every(([, active]) => active);
}

export function getMetricValue(
  unit: Units,
  metricStatusValue?: MetricStatusValue | null,
  showMax = false,
): MetricValue {
  const isEmpty = metricStatusValue?.value === null;
  if (!metricStatusValue || isEmpty) {
    return {
      value: null,
      state: null,
    };
  }
  return {
    value: metricStatusValue.value,
    state: metricStatusValue.state,
    max: showMax ? metricStatusValue.criticalThreshold : undefined,
    suffix: unitLabel[unit],
  };
}

export function getOperatingStatusValue(status?: HealthStatusEnum): boolean | null {
  switch (status) {
    case HealthStatusEnum.HEALTHY:
      return true;
    case HealthStatusEnum.DEGRADED:
    case HealthStatusEnum.KO:
      return false;
    case HealthStatusEnum.NA:
    default:
      return null;
  }
}

export const statusColorFromSensorStatus: Record<
  HealthStatusEnum | DataAvailabilityEnum | ConnectorStatusEnum,
  string
> = {
  DEGRADED: hexColor[ColorLabelEnum.ORANGE],
  HEALTHY: hexColor[ColorLabelEnum.GREEN],
  NA: hexColor[ColorLabelEnum.GRAY],
  KO: hexColor[ColorLabelEnum.RED],
  CONNECTED: hexColor[ColorLabelEnum.GREEN],
  WAITING: hexColor[ColorLabelEnum.ORANGE],
  TIMEOUT: hexColor[ColorLabelEnum.PURPLE],
  UNREACHABLE: hexColor[ColorLabelEnum.PURPLE],
  OK: hexColor[ColorLabelEnum.GREEN],
  DESYNCHRONIZED: hexColor[ColorLabelEnum.ORANGE],
  UNREADABLE: hexColor[ColorLabelEnum.ORANGE],
  NO_DATA: hexColor[ColorLabelEnum.ORANGE],
} as const;
